import { useRef, useState } from 'react';
import { translate } from '@washed/i18n';
import type { FormState } from '../routes/HomePage.js';
import { useReveal } from '../motion/useReveal.js';
import { Reveal } from '../motion/Reveal.js';
import { MaskedReveal } from '../motion/MaskedReveal.js';
import { brandify } from '../components/Brand.js';
import { QuartierCombobox } from '../components/QuartierCombobox.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';
import { FormField } from '../components/FormField.js';
import { useWaitlistForm } from '../components/useWaitlistForm.js';
import { ConsentCheckbox } from '../components/ConsentCheckbox.js';
import { LOME_QUARTIERS_SORTED } from '../data/lome-quartiers.js';
import styles from './Worker.module.css';

export interface WorkerApplication {
  name: string;
  phone: string;
  quartier: string;
  email?: string;
  source: 'worker';
  consentTerms: boolean;
  cfTurnstileResponse?: string;
}

export interface WorkerProps {
  locale: 'fr' | 'en';
  onSubmit?: (input: WorkerApplication) => void;
  state?: FormState;
  showForm?: boolean;
}

export function Worker({
  locale,
  onSubmit,
  state = { kind: 'idle' },
  showForm = true,
}: WorkerProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [quartier, setQuartier] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const t = (k: Parameters<typeof translate>[0]) => translate(k, locale);
  const ref = useRef<HTMLElement>(null);
  const inView = useReveal(ref as React.RefObject<Element | null>);
  const form = useWaitlistForm(state, locale);
  const busy = state.kind === 'loading' || form.awaitingChallenge;
  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.({
      name,
      phone,
      quartier,
      source: 'worker',
      consentTerms: consent,
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(form.token ? { cfTurnstileResponse: form.token } : {}),
    });
  };

  const cardContent =
    state.kind === 'success' ? (
      <p ref={form.successRef} tabIndex={-1} role="status" className={styles.successMsg}>
        {brandify(t('marketing.worker.card.success'))}
      </p>
    ) : (
      <form onSubmit={handle} className={styles.form}>
        <FormField label={t('marketing.form.label.name')}>
          {(control) => (
            <input
              {...control}
              type="text"
              autoComplete="given-name"
              placeholder={t('marketing.worker.card.placeholder.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={styles.input}
            />
          )}
        </FormField>
        <FormField
          label={t('marketing.form.label.phone')}
          hint={t('marketing.form.hint.phone')}
          error={form.phoneError}
        >
          {(control) => (
            <input
              {...control}
              ref={form.phoneRef}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
              placeholder={t('marketing.worker.card.placeholder.phone')}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                form.clearErrorFor('phone');
              }}
              className={styles.input}
            />
          )}
        </FormField>
        <FormField label={t('marketing.form.label.quartier')}>
          {(control) => (
            <QuartierCombobox
              inputId={control.id}
              ariaDescribedBy={control['aria-describedby']}
              value={quartier}
              onChange={setQuartier}
              options={LOME_QUARTIERS_SORTED}
              placeholder={t('marketing.worker.card.placeholder.quartier')}
              listLabel={t('marketing.form.label.quartier')}
              inputClassName={styles.input}
            />
          )}
        </FormField>
        <FormField label={t('marketing.form.label.email')} error={form.emailError}>
          {(control) => (
            <input
              {...control}
              ref={form.emailRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('marketing.worker.card.placeholder.email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                form.clearErrorFor('email');
              }}
              className={styles.input}
            />
          )}
        </FormField>
        <TurnstileWidget {...form.turnstile} />
        <ConsentCheckbox locale={locale} checked={consent} onChange={setConsent} />
        <button type="submit" className={styles.cta} disabled={busy}>
          {state.kind === 'loading'
            ? t('marketing.worker.card.loading')
            : form.awaitingChallenge
              ? t('marketing.form.verifying')
              : `${t('marketing.worker.card.submit')} →`}
        </button>
        {form.formError && (
          <p role="alert" className={styles.errorMsg}>
            {form.formError}
          </p>
        )}
      </form>
    );

  return (
    <section id="worker" className={`${styles.worker} ${inView ? styles.in : ''}`} ref={ref}>
      <div className={styles.grid}>
        <div className={styles.narrative}>
          <Reveal className={styles.eyebrow}>{t('marketing.worker.eyebrow')}</Reveal>
          <Reveal as="h2" variant="mask" className={styles.h2}>
            <MaskedReveal>{brandify(t('marketing.worker.h2'))}</MaskedReveal>
          </Reveal>
          <Reveal as="p" className={styles.lede} i={1}>
            {brandify(t('marketing.worker.lede'))}
          </Reveal>
          <ul className={styles.bullets}>
            {([1, 2, 3, 4, 5] as const).map((i) => (
              <li
                key={i}
                className={`${styles.bullet} ${inView ? styles.bulletIn : ''}`}
                style={{ transitionDelay: `${(i - 1) * 80}ms` }}
              >
                <span className={styles.bulletText}>
                  {brandify(t(`marketing.worker.bullet${i}`))}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.aside}>
          <img
            className={`${styles.portrait} ${inView ? styles.portraitIn : ''}`}
            src="/photos/portrait-640.webp"
            srcSet="/photos/portrait-640.webp 640w, /photos/portrait-1024.webp 1024w"
            sizes="(max-width: 900px) 100vw, 40vw"
            alt={t('marketing.worker.photo.alt')}
            loading="lazy"
            decoding="async"
            width={1122}
            height={1402}
          />
          {showForm ? (
            <div className={`${styles.card} ${inView ? styles.cardIn : ''}`}>
              <div className={styles.cardEyebrow}>{t('marketing.worker.card.eyebrow')}</div>
              <h3 className={styles.cardH3}>{brandify(t('marketing.worker.card.h3'))}</h3>
              {cardContent}
            </div>
          ) : (
            <a
              href="/devenir-laveuse"
              className={`${styles.applyLink} ${inView ? styles.cardIn : ''}`}
            >
              <span className={styles.applyLabel}>
                {t('marketing.worker.apply')}
                <span aria-hidden="true"> →</span>
              </span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
